import { Download, Mail, Phone } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { Section, SectionHeading } from "../components/Section";
import Reveal from "../components/Reveal";

/** Everything on this page is the marina's own published content: the live page
 *  copy, plus a faithful transcription of its scanned "Dry Storage Rates and
 *  Rules" sheet. The original scan stays linked as the authoritative source.
 *  No figure, term or date here is inferred, rounded or reworded. */
const SCAN = "/site-assets/storage-docking/storage-rates-scan.png";

const RULES = [
  "We require a four month minimum. Credit card on file for payments or additional services.",
  "Twenty four hour notice must be given. We will try to accommodate inside of 24 hours but no guarantee. Inside 24 hours there will be an additional $25 charge.",
  "Boat will be picked up and dropped off in the marina parking lot.",
  "If the boat is dropped off after marina closing hours it will be taken to storage the following day.",
  "If the owner wants a boat early before the marina opens, it will be brought to the marina parking lot the night before the date requested.",
  "West Branch Marina is not responsible for any damage or theft and the owner is responsible to carry proper insurance. Owners completely indemnify West Branch Marina.",
  "Owner is responsible to keep the trailer and boat license up to date and the trailer in good functioning order. This includes but not limited to lights, bearings, straps and tires.",
  "Any breakdown expenses that occur due to faulty equipment will be the owners responsibility. The marina has the right to make what decision they feel is best at the time of the breakdown.",
  "In/Out water service available at additional cost. If the boat doesn't start for any reason the boat will be taken to the marina parking lot and the owner will be notified. Owner will still be charged a regular In/Out service fee.",
];

/** Transcribed verbatim from the scan's "Rates and Packages" grid. */
const PACKAGES = [
  {
    name: "Basic",
    trips: "6 Trips Per Summer",
    additionalTrips: "$30 Per Trip",
    pricePerMonth: "$125.00",
    docks: "2 Weekday Nights and 2 Weekend Nights",
    docksNote: "$20 + tax for additional nights",
  },
  {
    name: "Premium",
    trips: "16 Trips Per Summer",
    additionalTrips: "$30.00 Per Trip",
    pricePerMonth: "$200.00",
    docks: "4 Weekday Nights and 6 Weekend Nights",
    docksNote: "$20 + tax for additional nights",
  },
];

const IN_OUT = [
  { label: "In Only", price: "$70.00" },
  { label: "Out Only", price: "$70.00" },
  { label: "In/Out", price: "$100.00" },
];

const ROWS = [
  { label: "Trips", key: "trips" },
  { label: "Additional Trips", key: "additionalTrips" },
  { label: "Price Per Month", key: "pricePerMonth" },
];

export default function StorageDocking() {
  return (
    <>
      <Seo
        title="Storage and Docking | West Branch Marina"
        description="West Branch and Lake Milton Marina dry storage and trailer storage, with dry storage rates, packages and rules."
        path="/storage-and-docking"
        image="/images/marina_docks.jpeg"
      />

      <PageHero
        image="/images/marina_docks.jpeg"
        alt="Docks and moored boats at the marina"
        eyebrow="Storage and Docking"
        title="West Branch and Lake Milton Marina Dry Storage and Trailer Storage"
      />

      <Section tone="white">
        <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-16">
          <Reveal>
            <p data-testid="storage-intro" className="text-base leading-relaxed text-slate-700">
              In addition to our summer dry storage, we also offer trailer storage for any boater with
              who has a dock. Trailer storage is $175 for outdoors or $200 for indoors. We do ask for 24
              hours notice for access to trailer.
            </p>
          </Reveal>
          <Reveal delay={90}>
            <dl data-testid="trailer-storage-rates" className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-sand-border bg-sand p-6">
                <dt className="text-xs font-bold uppercase tracking-[0.16em] text-lake">
                  Trailer storage — outdoors
                </dt>
                <dd className="mt-2 font-display text-3xl font-extrabold text-navy">$175</dd>
              </div>
              <div className="rounded-2xl border border-sand-border bg-sand p-6">
                <dt className="text-xs font-bold uppercase tracking-[0.16em] text-lake">
                  Trailer storage — indoors
                </dt>
                <dd className="mt-2 font-display text-3xl font-extrabold text-navy">$200</dd>
              </div>
            </dl>
          </Reveal>
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Dry storage" title="Rates and Packages" />

        {/* Desktop: the scan's grid, kept as a real table with row/col scopes. */}
        <Reveal className="mt-10 hidden overflow-hidden rounded-3xl border border-sand-border bg-white lg:block">
          <table data-testid="storage-packages-table" className="w-full border-collapse text-left">
            <caption className="sr-only">
              Dry storage rates and packages, transcribed from the marina's rate sheet
            </caption>
            <thead>
              <tr className="bg-navy text-white">
                <th scope="col" className="px-6 py-4">
                  <span className="sr-only">Item</span>
                </th>
                {PACKAGES.map((p) => (
                  <th key={p.name} scope="col" className="px-6 py-4 font-display text-lg font-bold">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.key} className="border-t border-sand-border">
                  <th scope="row" className="px-6 py-4 text-sm font-bold text-navy">
                    {r.label}
                  </th>
                  {PACKAGES.map((p) => (
                    <td
                      key={p.name}
                      data-testid={`storage-${p.name.toLowerCase()}-${r.key}`}
                      className="px-6 py-4 text-sm text-slate-700"
                    >
                      {p[r.key]}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-sand-border">
                <th scope="row" className="px-6 py-4 text-sm font-bold text-navy">
                  Complimentary Courtesy Docks
                </th>
                {PACKAGES.map((p) => (
                  <td key={p.name} className="px-6 py-4 text-sm text-slate-700">
                    {p.docks}
                    <span className="mt-1 block text-xs text-slate-500">{p.docksNote}</span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </Reveal>

        {/* Mobile: the same cells as stacked cards. A 3-column grid is unreadable
            at phone width; no value from the scan is dropped. */}
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:hidden">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.name} delay={i * 70}>
              <div
                data-testid={`storage-package-card-${p.name.toLowerCase()}`}
                className="h-full rounded-3xl border border-sand-border bg-white p-6"
              >
                <h3 className="font-display text-xl font-bold text-navy">{p.name}</h3>
                <dl className="mt-5 space-y-4 text-sm">
                  {ROWS.map((r) => (
                    <div key={r.key} className="flex items-baseline justify-between gap-4">
                      <dt className="font-semibold text-slate-500">{r.label}</dt>
                      <dd className="text-right font-bold text-navy">{p[r.key]}</dd>
                    </div>
                  ))}
                  <div>
                    <dt className="font-semibold text-slate-500">Complimentary Courtesy Docks</dt>
                    <dd className="mt-1 font-bold text-navy">{p.docks}</dd>
                    <dd className="mt-1 text-xs text-slate-500">{p.docksNote}</dd>
                  </div>
                </dl>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-8 rounded-3xl border border-sand-border bg-white p-6 lg:p-8">
          <h3 className="font-display text-lg font-bold text-navy">In/Out Water Service</h3>
          <dl data-testid="in-out-water-service" className="mt-5 grid gap-4 sm:grid-cols-3">
            {IN_OUT.map((s) => (
              <div key={s.label} className="rounded-2xl border border-sand-border bg-sand p-5">
                <dt className="text-xs font-bold uppercase tracking-[0.16em] text-lake">{s.label}</dt>
                <dd className="mt-2 font-display text-2xl font-extrabold text-navy">{s.price}</dd>
              </div>
            ))}
          </dl>
        </Reveal>

        <p data-testid="courtesy-dock-footnote" className="mt-6 text-sm leading-relaxed text-slate-600">
          *Owners will be charged the normal overnight courtesy dock rate of $20.00 Per Night for
          additional nights used beyond their complimentary nights.
        </p>

        <Reveal className="mt-8">
          <a
            href={SCAN}
            download
            data-testid="storage-scan-download"
            className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy-surface"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download the original rate sheet
          </a>
        </Reveal>
      </Section>

      <Section tone="white">
        <SectionHeading eyebrow="Dry storage" title="Rates and Rules" />
        <ol data-testid="storage-rules-list" className="mt-10 grid gap-4 lg:grid-cols-2">
          {RULES.map((rule, i) => (
            <Reveal
              key={i}
              delay={(i % 2) * 60}
              className="flex gap-4 rounded-2xl border border-sand-border bg-sand p-5"
            >
              <span className="font-display text-lg font-extrabold text-lake/60">{i + 1}</span>
              <p className="text-sm leading-relaxed text-slate-700">{rule}</p>
            </Reveal>
          ))}
        </ol>
        <Reveal className="mt-10">
          <a href={SCAN} target="_blank" rel="noreferrer" data-testid="storage-scan-link">
            <img
              src={SCAN}
              alt="Scanned West Branch Marina dry storage rates and rules sheet"
              data-testid="storage-scan-image"
              className="w-full rounded-3xl border border-sand-border"
              loading="lazy"
            />
          </a>
        </Reveal>
      </Section>

      <Section>
        <SectionHeading eyebrow="Docks" title="2026 Dock Lottery" />
        <div className="mt-10 grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:gap-14">
          <Reveal>
            <p className="text-base leading-relaxed text-slate-700">
              All docks located at both West Branch and Lake Milton Marinas are ran and operated by the
              Ohio Division of Parks and Watercraft.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-700">
              For any inquiries regarding the dock lottery or availability for the 2026 season contact:
            </p>
            <ul data-testid="dock-lottery-contacts" className="mt-5 space-y-3">
              <li>
                <a
                  href="tel:+13302350030"
                  data-testid="dock-lottery-phone"
                  className="inline-flex items-center gap-2 text-sm font-bold text-navy hover:text-ember"
                >
                  <Phone className="h-4 w-4 text-lake" aria-hidden="true" />
                  Park Office: 330-235-0030 ext. 2
                </a>
              </li>
              <li>
                <a
                  href="mailto:Tonya.carpenter@dnr.ohio.gov"
                  data-testid="dock-lottery-email"
                  className="inline-flex items-center gap-2 text-sm font-bold text-navy hover:text-ember"
                >
                  <Mail className="h-4 w-4 text-lake" aria-hidden="true" />
                  Email: Tonya.carpenter@dnr.ohio.gov
                </a>
              </li>
            </ul>
          </Reveal>
          <Reveal delay={90} className="rounded-3xl border border-sand-border bg-white p-7">
            <h3 data-testid="dock-lottery-heading" className="font-display text-lg font-bold text-navy">
              The 2026 dock lottery will take place via online submission.
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-slate-700">
              Please visit{" "}
              <a
                href="https://www.ohiodnr.gov/docklottery"
                target="_blank"
                rel="noreferrer"
                data-testid="dock-lottery-odnr-link"
                className="font-bold text-ember hover:underline"
              >
                https://www.ohiodnr.gov/docklottery
              </a>{" "}
              to register.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-slate-700">
              Dock lottery submissions will be accepted from October 1 - October 31, 2026.
            </p>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
